import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Typography, Space, Button, Spin, message, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, ExportOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { mocService, MocFormalReports, MocRegisterStats } from '../../services/moc.service';

const { Title, Text } = Typography;

const PRESET_LINKS: { label: string; pathSuffix: string }[] = [
  { label: 'Open changes', pathSuffix: '?preset=open' },
  { label: 'Pending approval', pathSuffix: '?preset=pending_approval' },
  { label: 'Pending verification', pathSuffix: '?preset=pending_verification' },
  { label: 'Risk assessment', pathSuffix: '?preset=pending_risk' },
  { label: 'Implementation', pathSuffix: '?preset=implementation' },
  { label: 'High risk (open)', pathSuffix: '?preset=high_risk' },
  { label: 'Customer notify (open)', pathSuffix: '?preset=customer_notification' },
];

const MOCReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<MocRegisterStats | null>(null);
  const [formal, setFormal] = useState<MocFormalReports | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, f] = await Promise.all([mocService.getRegisterStats(), mocService.getFormalReports()]);
        if (!cancelled) {
          setStats(s);
          setFormal(f);
        }
      } catch (e: unknown) {
        console.error(e);
        message.error('Failed to load MOC reports');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const exportCsv = async () => {
    try {
      const rows = await mocService.list();
      const headers = ['moc_number', 'moc_date', 'change_title', 'status', 'urgency', 'risk_score', 'cust_notify'];
      const lines = [
        headers.join(','),
        ...rows.map((r) =>
          [
            JSON.stringify(r.moc_number || ''),
            JSON.stringify(r.moc_date || ''),
            JSON.stringify(r.change_title || ''),
            JSON.stringify(r.status || ''),
            JSON.stringify(r.urgency || ''),
            r.risk_summary_score ?? '',
            r.customer_notification_required ? 'yes' : 'no',
          ].join(',')
        ),
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `moc-register-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Export started');
    } catch (e: unknown) {
      console.error(e);
      message.error('Export failed');
    }
  };

  const statusColumns: ColumnsType<{ status: string; count: number }> = [
    { title: 'Status', dataIndex: 'status', render: (s: string) => s.replace(/_/g, ' ') },
    { title: 'Count', dataIndex: 'count', width: 100 },
  ];

  const typeColumns: ColumnsType<{ change_type_id: string; code: string; name: string; count: number }> = [
    { title: 'Code', dataIndex: 'code', width: 120 },
    { title: 'Change type', dataIndex: 'name', ellipsis: true },
    { title: 'Count', dataIndex: 'count', width: 90 },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space align="start" style={{ width: '100%', justifyContent: 'space-between', marginBottom: 24 }} wrap>
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/grc/moc')}>
              Register
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              MOC reports &amp; KPIs
            </Title>
          </Space>
          <Space wrap>
            <Button icon={<ExportOutlined />} onClick={() => void exportCsv()}>
              Export full register (CSV)
            </Button>
          </Space>
        </Space>

        {loading || !stats ? (
          <Spin />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Statistic title="Total MOC records" value={stats.total} />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Statistic title="Open (not closed / rejected / cancelled)" value={stats.open} />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Statistic title="Pending approval" value={stats.pending_approval} />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Statistic title="Pending verification" value={stats.pending_verification} />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Statistic title="Risk assessment pending" value={stats.pending_risk} />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Statistic title="Implementation (approved + in progress)" value={stats.implementation_active} />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Statistic title="High risk open (score ≥ 12)" value={stats.high_risk_open} />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Statistic title="Customer notification (open)" value={stats.customer_notification_open} />
              </Col>
            </Row>

            {formal ? (
              <>
                <Title level={5} style={{ marginTop: 32 }}>
                  Open MOC aging (days since created)
                </Title>
                <Text type="secondary">Non-terminal statuses only — distribution by age.</Text>
                <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
                  <Col xs={24} sm={8}>
                    <Statistic title="0–7 days" value={formal.aging_open.days_0_7} />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic title="8–30 days" value={formal.aging_open.days_8_30} />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic title="31+ days" value={formal.aging_open.days_31_plus} />
                  </Col>
                </Row>

                <Title level={5} style={{ marginTop: 24 }}>
                  By change type (all time)
                </Title>
                <Table
                  size="small"
                  rowKey="change_type_id"
                  pagination={false}
                  columns={typeColumns}
                  dataSource={formal.by_change_type}
                />

                <Title level={5} style={{ marginTop: 24 }}>
                  By status (all time)
                </Title>
                <Table
                  size="small"
                  rowKey="status"
                  pagination={false}
                  columns={statusColumns}
                  dataSource={formal.by_status}
                />
              </>
            ) : null}

            <Title level={5} style={{ marginTop: 32 }}>
              Quick views
            </Title>
            <Text type="secondary">
              Opens the MOC register with the matching filter. Approval and implementation-line tasks appear in My Tasks
              after sync.
            </Text>
            <div style={{ marginTop: 16 }}>
              <Space wrap>
                {PRESET_LINKS.map((x) => (
                  <Link key={x.pathSuffix} to={`/grc/moc${x.pathSuffix}`}>
                    {x.label}
                  </Link>
                ))}
              </Space>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default MOCReportsPage;
